/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { UsersAndGroupsInGroupSelector } from "./UsersAndGroupsInGroupSelector";
import { useLazyGetTSAccessTokenQuery } from "../../../services/serverless";
import {
  useLazyListUsersQuery,
  useLazyListGroupsQuery,
} from "../../../services/thoughtSpotV1";
import { ReactSelectOption } from "../../Interfaces";
import { MakeNewGroup } from "./MakeNewGroup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { RelevantTSGroupInfo } from "../../../services/types/thoughtSpot";
import { getThoughtSpotAuthTokenFallback, getUserSessionItem } from "../../..";
import { useAppSelector } from "../../../app/hooks";
import { selectUserAuthContext } from "../../console/consoleSlice";

interface LiveboardPermissionToolProps {
  orgId: number;
  enableDescriptions: boolean;
  userFilter?: string;
}

interface LiveboardPermissionToolState {
  selectors: JSX.Element[];
  loading: boolean;
}

/**
 * Tool to check all groups in an Org, and update/delete them as necessary.
 * @param props
 * @returns
 */

export const GroupAccessTool = (props: LiveboardPermissionToolProps) => {
  const [state, setState] = useState<LiveboardPermissionToolState>({
    selectors: [],
    loading: true,
  });

  const [tsAccessTokenQuery, tsAccessTokenQueryResults] =
    useLazyGetTSAccessTokenQuery();
  const [listGroupsQuery, listGroupsQueryResults] = useLazyListGroupsQuery();
  const [listUsersQuery, listUsersQueryResults] = useLazyListUsersQuery();
  const userAuthContext = useAppSelector(selectUserAuthContext);
  const [tsToken, setTSToken] = useState<string>("");

  useEffect(() => {
    const handleToken = async () => {
      try {
        const callResult = await tsAccessTokenQuery(props.orgId).unwrap();
        if (callResult.token) {
          setTSToken(callResult.token);
          listGroupsQuery({ thoughtSpotAccessToken: callResult.token });
        } else {
          //using this instead of normal error handling because CORS errors on
          //some clients are not reported as errored.
          throw {};
        }
      } catch (e) {
        console.log("Using fallback token generator");
        const username =
          userAuthContext?.userName ?? getUserSessionItem("userName") ?? "";
        const fallbackResponse = await getThoughtSpotAuthTokenFallback(
          username,
          props.orgId,
        );
        setTSToken(fallbackResponse);
        listGroupsQuery({ thoughtSpotAccessToken: fallbackResponse });
      }
    };
    handleToken();
  }, []);

  useEffect(() => {
    if (
      tsToken &&
      listGroupsQueryResults.isSuccess &&
      listGroupsQueryResults.status === "fulfilled"
    ) {
      setState({ ...state, loading: true });
      listUsersQuery({
        thoughtSpotAccessToken: tsToken,
        filter: props.userFilter ?? "",
      });
    }
  }, [listGroupsQueryResults]);

  function formatGroups(data: RelevantTSGroupInfo[]): ReactSelectOption[] {
    return data.map((value) => {
      return {
        label: value.displayName,
        value: value.id,
      };
    });
  }

  useEffect(() => {
    if (
      listUsersQueryResults.isSuccess &&
      listUsersQueryResults.status === "fulfilled" &&
      listGroupsQueryResults.isSuccess &&
      listGroupsQueryResults.status === "fulfilled" &&
      tsToken
    ) {
      let formattedUsersList: ReactSelectOption[] = [];
      let formattedUsersInGroupList: ReactSelectOption[] = [];

      let formattedGroupsList: ReactSelectOption[] = [];
      let formattedGroupsInGroupList: ReactSelectOption[] = [];

      let selectorsArray: JSX.Element[] = [];

      formattedGroupsList = formatGroups(listGroupsQueryResults.data);

      listGroupsQueryResults.data.forEach((elementGroup) => {
        listUsersQueryResults.data.forEach((elementUser) => {
          formattedUsersList.push({
            label: elementUser.displayName,
            value: elementUser.id,
          });
          if (elementUser.groupNames.includes(elementGroup.name)) {
            formattedUsersInGroupList.push({
              label: elementUser.displayName,
              value: elementUser.id,
            });
          }
        });
        //we go through every group and find which ones have this current group
        //as a master

        let childGroups: RelevantTSGroupInfo[] = [];
        listGroupsQueryResults.data.forEach((group) => {
          if (
            group.groupNames &&
            group.groupNames.length > 0 &&
            group.groupNames.includes(elementGroup.displayName)
          ) {
            childGroups.push(group);
          }
        });

        formattedGroupsInGroupList = formatGroups(childGroups);

        selectorsArray.push(
          <UsersAndGroupsInGroupSelector
            key={elementGroup.displayName}
            orgId={props.orgId}
            selectableUsers={{ text: "Users", content: formattedUsersList }}
            selectableGroups={{ text: "Groups", content: formattedGroupsList }}
            tsAccessToken={tsToken}
            group={{
              text: "Group",
              description: props.enableDescriptions ? elementGroup.description : "",
              content: {
                label: elementGroup.displayName,
                value: elementGroup.id,
              },
              internalTSLabel: elementGroup.name,
            }}
            preselectedMultiplesUsers={formattedUsersInGroupList}
            preselectedMultiplesGroups={formattedGroupsInGroupList}
            deletionCallback={() =>
              listGroupsQuery({
                thoughtSpotAccessToken: tsToken,
              })
            }
          />,
        );
        formattedUsersList = [];
        formattedUsersInGroupList = [];
      });
      setState({ ...state, selectors: selectorsArray, loading: false });
    }
  }, [listUsersQueryResults, listGroupsQueryResults]);

  return (
    <div className="module-master">
      <div className="module-title">Group Access</div>
      {state.loading && (
        <div className="module-loading">
          <FontAwesomeIcon
            icon={faSpinner}
            size="2x"
            spin={true}
            style={{ margin: "0 auto" }}
          />
        </div>
      )}
      {!state.loading && tsToken && (
        <div>
          <MakeNewGroup
            tsAccessToken={tsToken}
            orgId={props.orgId}
            creationCallback={() => {
              if (tsToken) {
                listGroupsQuery({
                  thoughtSpotAccessToken: tsToken,
                });
              }
            }}
          />
          <div className="container-fluid mt-2">
            <div className="row">{state.selectors}</div>
          </div>
        </div>
      )}
    </div>
  );
};
